/**
 * El pedido del vídeo del Gran Libro del Polo Norte (`BOOK_STORY`): lo que el
 * padre rellena y lo que el guion puede gastar. A diferencia de la carta no
 * hay listas de regalos —el niño aún no ha escrito la suya— y sí un reto
 * opcional, que Papá Noel cuenta siempre en positivo.
 *
 * Los campos comunes, sus topes y sus lectores son los de `form-fields.ts`.
 */
import { asFormObject, list, MAX_LENGTH, readCommonFields, text, type BaseOrderForm } from './form-fields'

export interface BookOrderForm extends BaseOrderForm {
  /**
   * Opcional: algo en lo que los padres quieren animarle. La CLAVE es
   * obligatoria (`undefined` si no hay reto) a propósito: con `challenge?` un
   * formulario de carta encajaba estructuralmente en éste, y un pedido de
   * libro habría aceptado una carta sin que el tipo se quejara. En el JSON no
   * se nota: `JSON.stringify` omite el `undefined`.
   */
  challenge: string | undefined
}

/** `elves-notes` gasta un hecho personal: sin ninguno no hay escena. */
export const BOOK_MIN_PERSONAL_DETAILS = 1

/** Cuántas entradas de cada lista gasta el guion del libro. */
export const BOOK_MAX_ENTRIES = { interests: 4, personalDetails: 3 } as const

/**
 * Intereses mínimos: el saludo gasta uno y, sin reto, la escena `challenge`
 * gasta otro distinto. Con reto, ésa gasta el reto y basta uno.
 */
export function minInterestsFor(challenge: string | undefined): number {
  return challenge ? 1 : 2
}

function assertInterests(form: Record<string, unknown>, challenge: string | undefined): string[] {
  const interests = list(form.interests, 'interests', BOOK_MAX_ENTRIES.interests)
  const min = minInterestsFor(challenge)
  if (interests.length < min) throw new Error(`El pedido necesita al menos ${min} intereses${challenge ? '' : ' si no trae reto'}.`)
  return interests
}

function assertPersonalDetails(form: Record<string, unknown>): string[] {
  const personalDetails = list(form.personalDetails, 'personalDetails', BOOK_MAX_ENTRIES.personalDetails)
  if (personalDetails.length < BOOK_MIN_PERSONAL_DETAILS) throw new Error('El pedido necesita al menos un hecho personal.')
  return personalDetails
}

/** Mismo patrón que `assertVideoOrderForm`: se RECHAZA lo que no cabe, nunca se trunca. */
export function assertBookOrderForm(value: unknown): BookOrderForm {
  const form = asFormObject(value)
  const challenge = text(form.challenge, 'challenge', MAX_LENGTH.listEntry)
  const common = readCommonFields(form)
  return {
    name: common.name,
    age: common.age,
    location: common.location,
    interests: assertInterests(form, challenge),
    personalDetails: assertPersonalDetails(form),
    challenge,
    parentMessage: common.parentMessage,
    photoPath: common.photoPath,
    language: common.language,
    tone: common.tone,
  }
}
